/**
 * Custom tracking domain — operator page (FS-674).
 *
 * Surface for the /api/v1/custom-tracking-domain backend. Agencies register
 * their own branded CNAME (e.g. app.brand.com) so tracking URLs show their
 * hostname instead of the platform's.
 *
 *   1. Add-domain form — type an FQDN and submit. Client-side FQDN + reserved
 *      suffix validation mirrors the server so typos fail before POST.
 *
 *   2. Domain list — one row per registered domain with status chip (emerald
 *      when verified, zinc otherwise), created timestamp, verify and remove
 *      actions.
 *
 * Brand: emerald → sky gradient headline, Inter, zinc borders, 6-8px rounded,
 * light theme only. No emojis, no competitor names.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	countVerified,
	createDomain,
	customTrackingDomainQueryKeys,
	deleteDomain,
	domainDraftSchema,
	EMPTY_DRAFT,
	formatTimestamp,
	listDomains,
	removeDomain,
	sortDomains,
	statusBadgeClass,
	statusLabel,
	upsertDomain,
	verifyDomain,
	type CustomTrackingDomainView,
	type DomainDraft,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens (match brand rules)
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function CustomTrackingDomainPage() {
	const queryClient = useQueryClient();

	const [draft, setDraft] = useState<DomainDraft>(EMPTY_DRAFT);
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [busyId, setBusyId] = useState<string | null>(null);

	const listQuery = useQuery({
		queryKey: customTrackingDomainQueryKeys.list(),
		queryFn: listDomains,
	});

	const createMutation = useMutation({
		mutationFn: createDomain,
		onSuccess: (row) => {
			toast.success(`Registered ${row.domain}. Point its CNAME to track.trackerity.com.`);
			setDraft(EMPTY_DRAFT);
			setDraftErrors({});
			queryClient.setQueryData<CustomTrackingDomainView[]>(
				customTrackingDomainQueryKeys.list(),
				(prev) => (prev ? upsertDomain(prev, row) : [row]),
			);
			queryClient.invalidateQueries({
				queryKey: customTrackingDomainQueryKeys.list(),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to register domain.");
		},
	});

	const verifyMutation = useMutation({
		mutationFn: (id: string) => verifyDomain(id),
		onMutate: (id) => {
			setBusyId(id);
		},
		onSettled: () => {
			setBusyId(null);
		},
		onSuccess: (row) => {
			toast.success(`Verified ${row.domain}.`);
			queryClient.setQueryData<CustomTrackingDomainView[]>(
				customTrackingDomainQueryKeys.list(),
				(prev) => (prev ? upsertDomain(prev, row) : [row]),
			);
			queryClient.invalidateQueries({
				queryKey: customTrackingDomainQueryKeys.list(),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Verification failed.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteDomain(id),
		onMutate: (id) => {
			setBusyId(id);
		},
		onSettled: () => {
			setBusyId(null);
		},
		onSuccess: (_void, id) => {
			toast.success("Domain removed.");
			queryClient.setQueryData<CustomTrackingDomainView[]>(
				customTrackingDomainQueryKeys.list(),
				(prev) => (prev ? removeDomain(prev, id) : prev),
			);
			queryClient.invalidateQueries({
				queryKey: customTrackingDomainQueryKeys.list(),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove domain.");
		},
	});

	const sorted = useMemo(
		() => (listQuery.data ? sortDomains(listQuery.data) : []),
		[listQuery.data],
	);

	const totals = useMemo(() => countVerified(sorted), [sorted]);

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const parsed = domainDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		createMutation.mutate(parsed.data);
	}

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 pb-16 pt-6">
			<header className="flex flex-col gap-2">
				<h1 className="font-inter bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
					Custom tracking domain
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Serve tracking clicks under your own hostname. Register a
					fully-qualified domain (e.g. <span className="font-mono">app.brand.com</span>),
					add a CNAME record pointing at <span className="font-mono">track.trackerity.com</span>,
					then click Verify once the DNS entry is live.
				</p>
			</header>

			<section aria-labelledby="add-domain-heading" className={`${CARD} p-6`}>
				<h2 id="add-domain-heading" className="text-base font-semibold text-zinc-900">
					Register a domain
				</h2>
				<form
					onSubmit={handleSubmit}
					className="mt-4 flex flex-col gap-4 md:flex-row md:items-start md:gap-4"
				>
					<div className="flex w-full flex-col gap-1">
						<label htmlFor="domain" className={LABEL}>
							Domain (FQDN)
						</label>
						<input
							id="domain"
							name="domain"
							type="text"
							className={INPUT}
							placeholder="app.brand.com"
							value={draft.domain}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, domain: e.target.value }))
							}
							disabled={createMutation.isPending}
							autoComplete="off"
							spellCheck={false}
						/>
						{draftErrors.domain ? (
							<span className={ERROR_TEXT}>{draftErrors.domain}</span>
						) : (
							<span className={HINT}>
								Must be a fully-qualified hostname — no scheme, port, or path.
							</span>
						)}
					</div>
					<div className="flex items-end">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={createMutation.isPending}
						>
							{createMutation.isPending ? "Registering…" : "Register domain"}
						</button>
					</div>
				</form>
			</section>

			<section aria-labelledby="list-heading" className={`${CARD} p-6`}>
				<div className="flex items-center justify-between">
					<h2 id="list-heading" className="text-base font-semibold text-zinc-900">
						Registered domains
					</h2>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
					>
						{listQuery.isFetching ? "Refreshing…" : "Refresh"}
					</button>
				</div>

				{sorted.length > 0 ? (
					<div className="mt-4 grid grid-cols-3 gap-3 md:max-w-sm">
						<StatCell label="Total" value={totals.total} />
						<StatCell label="Verified" value={totals.verified} />
						<StatCell label="Pending" value={totals.pending} />
					</div>
				) : null}

				<div className="mt-6 overflow-hidden rounded-[6px] border border-zinc-200">
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
								<th className="px-4 py-2 font-medium">Domain</th>
								<th className="px-4 py-2 font-medium">Status</th>
								<th className="px-4 py-2 font-medium">Created</th>
								<th className="px-4 py-2 font-medium">Updated</th>
								<th className="px-4 py-2 font-medium text-right">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{listQuery.isLoading ? (
								<tr>
									<td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">
										Loading domains…
									</td>
								</tr>
							) : listQuery.isError ? (
								<tr>
									<td colSpan={5} className="px-4 py-8 text-center text-sm text-red-600">
										{(listQuery.error as Error | undefined)?.message ?? "Unable to load domains."}
									</td>
								</tr>
							) : sorted.length === 0 ? (
								<tr>
									<td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">
										No custom tracking domains yet. Register one above to get started.
									</td>
								</tr>
							) : (
								sorted.map((d) => (
									<tr key={d.id}>
										<td className="break-all px-4 py-3 font-mono text-zinc-900">
											{d.domain}
										</td>
										<td className="px-4 py-3">
											<span
												className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(d.verified)}`}
											>
												{statusLabel(d.verified)}
											</span>
										</td>
										<td className="px-4 py-3 text-zinc-700">
											{formatTimestamp(d.created_at)}
										</td>
										<td className="px-4 py-3 text-zinc-700">
											{formatTimestamp(d.updated_at)}
										</td>
										<td className="px-4 py-3 text-right">
											<div className="inline-flex gap-2">
												<button
													type="button"
													className={BTN_SECONDARY}
													onClick={() => verifyMutation.mutate(d.id)}
													disabled={
														d.verified ||
														(verifyMutation.isPending && busyId === d.id)
													}
												>
													{verifyMutation.isPending && busyId === d.id
														? "Verifying…"
														: d.verified
															? "Verified"
															: "Verify"}
												</button>
												<button
													type="button"
													className={BTN_DANGER}
													onClick={() => {
														if (
															typeof window !== "undefined" &&
															!window.confirm(
																`Remove ${d.domain}? Clicks served through this hostname will stop resolving.`,
															)
														) {
															return;
														}
														deleteMutation.mutate(d.id);
													}}
													disabled={deleteMutation.isPending && busyId === d.id}
												>
													{deleteMutation.isPending && busyId === d.id
														? "Removing…"
														: "Remove"}
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Local presentational widgets
// ---------------------------------------------------------------------------

function StatCell({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2">
			<div className="text-xs font-medium text-zinc-500">{label}</div>
			<div className="text-lg font-semibold text-zinc-900">{value}</div>
		</div>
	);
}
